import { Mapper } from '@intelliarts/ia-react-template';

import {
  assertHeadcountApiValid,
  headcountDashboardApiBodySchema,
  headcountYearlyTrendEnvelopeSchema,
} from './headcountSchemas';
import type {
  HeadcountDashboardApiBody,
  HeadcountDashboard,
  HeadcountYearlyTrendApiResponse,
  HeadcountYearlyTrendPoint,
  HeadcountYearSnapshot,
} from './headcountTypes';

const EMPTY_INACTIVE = { maternity: 0, military: 0, sabbatical: 0 };

function parseHeadcountDashboardBody(body: HeadcountDashboardApiBody): HeadcountDashboard {
  const parsed = assertHeadcountApiValid(headcountDashboardApiBodySchema, body);

  const snapshot: HeadcountYearSnapshot = {
    year: parsed.year,
    activeEmployees: parsed.activeEmployees,
    inactive: parsed.inactive,
    averageTenureYears: parsed.averageTenureYears,
    newJoinersInYear: parsed.newJoinersInYear,
    benchEmployees: parsed.benchEmployees ?? 0,
    priorActiveEmployees: parsed.priorActiveEmployees ?? 0,
    priorInactive: parsed.priorInactive ?? EMPTY_INACTIVE,
    priorAverageTenureYears: parsed.priorAverageTenureYears ?? 0,
    priorNewJoinersInYear: parsed.priorNewJoinersInYear ?? 0,
    priorMonthBenchEmployees: parsed.priorMonthBenchEmployees ?? 0,
    avgMonthlyHeadcountSelectedYear: parsed.avgMonthlyHeadcountSelectedYear,
    avgMonthlyHeadcountPriorYear: parsed.avgMonthlyHeadcountPriorYear,
    gender: parsed.gender,
    location: parsed.location,
    tenure: parsed.tenure,
    mom: parsed.mom.map((row) => ({
      month: row.month,
      active: row.active,
      bench: row.bench ?? 0,
      inactive: row.inactive,
    })),
    projects: parsed.projects ?? [],
    projectGroups: parsed.projectGroups ?? [],
  };

  const trend = parsed.yearlyTrend;
  const yearlyTrend =
    trend != null && trend.length > 0 ? trend : undefined;

  return { snapshot, yearlyTrend };
}

export const headcountDashboardMapper: Mapper<HeadcountDashboard, HeadcountDashboardApiBody> = {
  toModel: entity => {
    if (entity == null || typeof entity !== 'object') {
      return undefined as unknown as HeadcountDashboard;
    }
    return parseHeadcountDashboardBody(entity);
  },
  toEntity: model => ({
    ...model.snapshot,
    ...(model.yearlyTrend?.length ? { yearlyTrend: model.yearlyTrend } : {}),
  }),
};

export const headcountYearlyTrendEnvelopeMapper: Mapper<
  HeadcountYearlyTrendPoint[],
  HeadcountYearlyTrendApiResponse
> = {
  toModel: entity =>
    assertHeadcountApiValid(headcountYearlyTrendEnvelopeSchema, entity).data,
  toEntity: points => ({ data: points }),
};
